/**
 * Leads y consultas de una lista (F1–F3): helpers puros. Contrato:
 * argenlotapi/docs/frontend/FRONTEND_LEADS.md § Leads por colección.
 * Map: docs/maps/leads.md § Leads y consultas de la lista.
 *
 * Interno: `list` (lista común, `list_id`) y `showroom` (lista CAD,
 * `project_id`). En la UI las dos dicen **lista**.
 */
import { LEAD_COUNTS_MAX_IDS } from '../services/leadsService';

/** Etiqueta para consultas / ítems de un lote compartido solo (`lot_share`). */
export const LOT_SHARE_LABEL = 'Lote compartido';

/** Opción del filtro Origen que usa `via=lot_share` (no `source`). */
export const LEAD_VIA_LOT_SHARE_OPTION = Object.freeze({ id: 'via:lot_share', via: 'lot_share', label: LOT_SHARE_LABEL });

/** Botón del detalle de lista (owner / editor de Espacio). */
export const COLLECTION_LEADS_BUTTON_LABEL = 'Leads y consultas';

/**
 * Acceso a CollectionLeadsDialog desde Listas (detalle: botón/badge + ⋮;
 * también ⋮ de cards en el índice y CAD).
 * Oculto por decisión de la meet del 2026-10-15: no se usa por ahora
 * (utilidad muy puntual). Reactivar si se necesita.
 */
export const SHOW_COLLECTION_LEADS_FROM_LIST_DETAIL = false;

function readId(value) {
  return value == null ? '' : String(value).trim();
}


/**
 * Ids para el pedido único de `getleadcounts`. Si son más de 50, la lista
 * abierta (`focus`) va primero para que no quede afuera; si no, el orden
 * natural (así abrir el detalle no cambia el pedido y pega en la caché).
 *
 * @param {{ listIds?: string[], projectIds?: string[],
 *   focus?: { kind: string, id: string }|null }} input
 * @returns {{ listIds: string[], projectIds: string[] }}
 */
export function orderLeadCountsIds({ listIds = [], projectIds = [], focus = null } = {}) {
  const uniq = (ids) => Array.from(new Set((Array.isArray(ids) ? ids : []).map(readId).filter(Boolean)));
  let lists = uniq(listIds);
  let projects = uniq(projectIds);
  const focusId = readId(focus?.id);
  if (!focusId || lists.length + projects.length <= LEAD_COUNTS_MAX_IDS) {
    return { listIds: lists, projectIds: projects };
  }
  if (focus?.kind === 'showroom') {
    projects = [focusId, ...projects.filter((id) => id !== focusId)];
    // Las CAD van después de las comunes en el pedido: hacerle lugar.
    lists = lists.slice(0, Math.max(0, LEAD_COUNTS_MAX_IDS - 1));
  } else {
    lists = [focusId, ...lists.filter((id) => id !== focusId)];
  }
  return { listIds: lists, projectIds: projects };
}

/**
 * Qué muestra el detalle de una lista: botón “Leads y consultas” (con badge
 * `unseen_leads`) o el badge de consultas. Ambos abren `CollectionLeadsDialog`.
 *
 * Modo leads solo si: el contexto lo permite (`eligible`: lista propia o del
 * Espacio, no compartida conmigo), `getleadcounts` respondió OK **con** esa
 * colección (no en `ignored_ids`) y la bandeja no dijo `can_view_leads:
 * false`. Cualquier otra cosa (404 sin ruta, 403, 503, red, API vieja) →
 * badge de consultas.
 *
 * @param {{ eligible: boolean, status: string, counts: object|null,
 *   canViewLeads?: boolean|null }} input
 * @returns {{ mode: 'leads', unseen: number, total: number }|{ mode: 'inquiries' }}
 */
export function resolveCollectionLeadsEntry({ eligible, status, counts, canViewLeads = null }) {
  if (!eligible || status !== 'ok' || !counts || canViewLeads === false) return { mode: 'inquiries' };
  const unseen = Number(counts.unseenLeads);
  const total = Number(counts.leads);
  return {
    mode: 'leads',
    unseen: Number.isFinite(unseen) && unseen > 0 ? Math.trunc(unseen) : 0,
    total: Number.isFinite(total) && total > 0 ? Math.trunc(total) : 0,
  };
}

/** Tooltip / aria del badge: “1 lead con consultas sin ver”. */
export function describeUnseenLeads(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n === 1 ? '1 lead con consultas sin ver' : `${Math.trunc(n)} leads con consultas sin ver`;
}

/**
 * Nombre de la colección de una consulta en la ficha del lead. Lote
 * compartido solo (`collection_kind: lot_share`) → “Lote compartido”; el
 * resto como antes (nombre, o “Lista” si se borró).
 *
 * @param {object} inquiry Fila de `getlead.inquiries[]`.
 * @returns {string}
 */
export function getInquiryCollectionLabel(inquiry) {
  if (readId(inquiry?.collection_kind) === 'lot_share') return LOT_SHARE_LABEL;
  return readId(inquiry?.collection_name) || 'Lista';
}

/**
 * ¿Hay consultas sin ver en el detalle del lead? `inquiries[]` sin `seen_at`
 * o, si la API lo trae, `observed_profile.inquiries.unseen` / el contador de
 * la tarjeta. Sin datos → false (no se llama a la API).
 *
 * @param {object} lead
 * @param {number} [cardUnseen] `unseen_inquiry_count` de la tarjeta, si se sabe.
 * @returns {boolean}
 */
export function leadHasUnseenInquiries(lead, cardUnseen = 0) {
  if (Number(cardUnseen) > 0) return true;
  const profileUnseen = Number(lead?.observed_profile?.inquiries?.unseen);
  if (Number.isFinite(profileUnseen) && profileUnseen > 0) return true;
  const inquiries = Array.isArray(lead?.inquiries) ? lead.inquiries : [];
  return inquiries.some((inq) => inq && !inq.seen_at);
}
